/**
 * Multi-author workspace hooks.
 *
 * `isDefault` remains a baseline/default-copy marker. The active content-plan
 * workspace comes from `?author=<id>` or `?author=all`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authorFirestoreService, type AuthorDoc } from '../services/AuthorFirestoreService';

export interface UseAuthorReturn {
  author: AuthorDoc | null;
  isComplete: boolean;
  loading: boolean;
  error: Error | null;
}

export interface UseAuthorListReturn {
  authors: AuthorDoc[];
  defaultAuthor: AuthorDoc | null;
  loading: boolean;
  error: Error | null;
}

export interface UseCurrentAuthorReturn extends UseAuthorReturn {
  authorId: string | null;
  requestedAuthorId: string | null;
  isOverview: boolean;
  authors: AuthorDoc[];
  defaultAuthor: AuthorDoc | null;
}

export interface UseAuthorWorkspaceReturn extends UseCurrentAuthorReturn {
  navigateToAuthor: (
    authorId: string,
    opts?: { preserveTab?: boolean; replace?: boolean; preserveContext?: boolean },
  ) => void;
  setDefaultAuthor: (authorId: string) => Promise<void>;
}

const LAST_AUTHOR_STORAGE_PREFIX = 'content-plan:last-author:';

function lastAuthorStorageKey(projectId: string): string {
  return `${LAST_AUTHOR_STORAGE_PREFIX}${projectId}`;
}

function readLastAuthorId(projectId: string | null | undefined): string | null {
  if (!projectId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(lastAuthorStorageKey(projectId));
  } catch {
    return null;
  }
}

function writeLastAuthorId(projectId: string | null | undefined, authorId: string): void {
  if (!projectId || authorId === 'all' || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(lastAuthorStorageKey(projectId), authorId);
  } catch {
    // Local storage can be disabled; URL-driven author selection still works.
  }
}

function clearLastAuthorId(projectId: string | null | undefined): void {
  if (!projectId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(lastAuthorStorageKey(projectId));
  } catch {
    // Ignore storage errors.
  }
}

export function useAuthorList(projectId: string | null | undefined): UseAuthorListReturn {
  const [authors, setAuthors] = useState<AuthorDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setAuthors([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const unsubscribe = authorFirestoreService.subscribeAllAuthors(
        projectId,
        (next) => {
          setAuthors(next);
          setLoading(false);
        },
      );
      return unsubscribe;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
      return undefined;
    }
  }, [projectId]);

  const defaultAuthor = useMemo(
    () => authors.find((author) => author.isDefault) ?? authors[0] ?? null,
    [authors],
  );

  return { authors, defaultAuthor, loading, error };
}

export function useCurrentAuthor(projectId: string | null | undefined): UseCurrentAuthorReturn {
  const location = useLocation();
  const { authors, defaultAuthor, loading, error } = useAuthorList(projectId);
  const requestedAuthorId = useMemo(
    () => new URLSearchParams(location.search).get('author'),
    [location.search],
  );
  const isOverview = requestedAuthorId === 'all';
  const author = useMemo(() => {
    if (!requestedAuthorId || isOverview) return null;
    return authors.find((candidate) => candidate.id === requestedAuthorId) ?? null;
  }, [authors, isOverview, requestedAuthorId]);

  return {
    authors,
    defaultAuthor,
    requestedAuthorId,
    authorId: isOverview ? 'all' : author?.id ?? null,
    author,
    isOverview,
    isComplete: isOverview ? false : !!author?.isComplete,
    loading,
    error,
  };
}

export function useAuthorWorkspace(projectId: string | null | undefined): UseAuthorWorkspaceReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useCurrentAuthor(projectId);

  const navigateToAuthor = useCallback((
    authorId: string,
    opts?: { preserveTab?: boolean; replace?: boolean; preserveContext?: boolean },
  ) => {
    writeLastAuthorId(projectId, authorId);
    const params = new URLSearchParams(location.search);
    const cpTab = params.get('cpTab');
    const next = opts?.preserveContext ? new URLSearchParams(params) : new URLSearchParams();
    next.set('author', authorId);
    if (opts?.preserveContext) {
      if (opts?.preserveTab === false) next.delete('cpTab');
    } else if (opts?.preserveTab !== false && cpTab) {
      next.set('cpTab', cpTab);
    }
    navigate(`${location.pathname}?${next.toString()}`, { replace: opts?.replace ?? false });
  }, [location.pathname, location.search, navigate, projectId]);

  useEffect(() => {
    if (!projectId || current.loading || current.requestedAuthorId) return;
    const lastAuthorId = readLastAuthorId(projectId);
    if (!lastAuthorId || lastAuthorId === 'all') return;

    const authorStillExists = current.authors.some((candidate) => candidate.id === lastAuthorId);
    if (!authorStillExists) {
      if (current.authors.length > 0) clearLastAuthorId(projectId);
      return;
    }

    navigateToAuthor(lastAuthorId, { preserveTab: true, replace: true, preserveContext: true });
  }, [
    current.authors,
    current.loading,
    current.requestedAuthorId,
    navigateToAuthor,
    projectId,
  ]);

  const setDefaultAuthor = useCallback(async (authorId: string) => {
    if (!projectId) throw new Error('Missing projectId');
    await authorFirestoreService.setDefaultAuthor(projectId, authorId);
  }, [projectId]);

  return {
    ...current,
    navigateToAuthor,
    setDefaultAuthor,
  };
}

/**
 * Compatibility facade for legacy components. Inside Content Plan it resolves
 * the URL-selected author; outside a workspace URL it falls back to the
 * baseline default so old setup banners continue to render while the page is
 * being migrated.
 */
export function useAuthor(projectId: string | null | undefined): UseAuthorReturn {
  const current = useCurrentAuthor(projectId);
  const author = current.author ?? (!current.requestedAuthorId ? current.defaultAuthor : null);

  return {
    author,
    isComplete: !!author?.isComplete,
    loading: current.loading,
    error: current.error,
  };
}
